/**
 * Norwegian (bokmål) user-facing strings.
 *
 * This is the SINGLE source of truth for every visible string in the web
 * UI. Anything a user reads — headings, buttons, placeholders, validation
 * messages, badge labels, status text, empty-state copy, ARIA labels — must
 * live here. See `apps/web/src/i18n/AGENTS.md` for the rules.
 *
 * Structure: grouped by feature/screen, with a small `common` namespace for
 * verbs/labels reused across screens. Parameterized strings are exposed as
 * functions so call-sites stay typed.
 *
 * Adding a new locale: copy this file (e.g. `en.ts`), translate the
 * values, keep the keys identical (TypeScript will enforce parity via the
 * `Strings` type), and wire it into `./index.ts`.
 */

export const nb = {
  app: {
    name: "Fellesen",
    tagline: "Felles oversikt. Felles oppgjør.",
    brandInitial: "F",
  },

  common: {
    cancel: "Avbryt",
    close: "Lukk",
    save: "Lagre",
    create: "Opprett",
    delete: "Slett",
    edit: "Rediger",
    remove: "Fjern",
    more: "Mer",
    loading: "Laster…",
    or: "eller",
    you: "deg",
    unknown: "Ukjent",
    showPassword: "Vis passord",
    hidePassword: "Skjul passord",
  },

  roles: {
    owner: "Eier",
    member: "Medlem",
  },

  time: {
    today: "i dag",
    yesterday: "i går",
    tomorrow: "i morgen",
    daysAgo: (n: number) => `for ${n} dager siden`,
    weeksAgo: (n: number) => (n === 1 ? "for 1 uke siden" : `for ${n} uker siden`),
    daysFromNow: (n: number) => (n === 1 ? "om 1 dag" : `om ${n} dager`),
    weeksFromNow: (n: number) => (n === 1 ? "om 1 uke" : `om ${n} uker`),
  },

  validation: {
    required: "Må fylles ut.",
    invalidType: "Ugyldig verdi.",
    email: "Skriv inn en gyldig e-postadresse.",
    minLength: (n: number) => (n === 1 ? "Må fylles ut." : `Må være minst ${n} tegn.`),
    maxLength: (n: number) => `Kan ikke være lengre enn ${n} tegn.`,
    minValue: (n: number) => `Må være minst ${n}.`,
    maxValue: (n: number) => `Kan ikke være større enn ${n}.`,
    passwordTooShort: "Passordet må være minst 10 tegn.",
    passwordsMustMatch: "Passordene må være like.",
    endDateAfterStart: "Forfallsdato må være etter startdato.",
    startDateNotFutureMonth: "Startdato kan ikke være i en fremtidig måned.",
    invalidDate: "Ugyldig dato.",
    invalidNumber: "Skriv inn et tall.",
    amountPositive: "Beløp må være større enn 0.",
    amountNonZero: "Beløp må være større eller mindre enn 0.",
    fileRequired: "Velg en fil.",
    fileNotPdf: "Filen må være en PDF.",
    fileTooLarge: (sizeMb: number) => `Filen er større enn ${sizeMb} MB.`,
    splitMismatch: (diff: string) => `Fordelingen må stemme med beløpet (${diff} kr unna).`,
    splitEmpty: "Minst én person må være med på fordelingen.",
    selectMember: "Velg et medlem.",
    selectOption: "Velg et alternativ.",
  },

  login: {
    googleSignIn: "Logg inn med Google",
    localAdminHeading: "Lokal admin",
    devOnlyBadge: "Kun dev",
    emailLabel: "E-post",
    emailPlaceholder: "navn@example.com",
    passwordLabel: "Passord",
    passwordPlaceholder: "Minst 10 tegn",
    submitButton: "Logg inn",
    noMethodsConfigured: "Ingen innloggingsmetoder konfigurert.",
    noAccountPrompt: "Har du ikke en konto?",
    registerLink: "Registrer deg",
    forgotPassword: "Glemt passord?",
    backToLogin: "Tilbake til innlogging",
  },

  register: {
    title: "Opprett konto",
    description: "Lag en konto for å bli med i en husstand og dele utgifter.",
    googleSignUp: "Registrer med Google",
    emailLabel: "E-post",
    emailPlaceholder: "navn@example.com",
    emailLockedHint: "E-posten kommer fra invitasjonen og kan ikke endres.",
    displayNameLabel: "Navn",
    displayNamePlaceholder: "F.eks. Ola",
    passwordLabel: "Passord",
    passwordPlaceholder: "Minst 10 tegn",
    passwordHint: "Minst 10 tegn. Lengre er bedre — vurder en passfrase.",
    submitButton: "Opprett konto",
    submitting: "Oppretter…",
    alreadyHaveAccount: "Har du allerede en konto?",
    loginLink: "Logg inn",
    invitationBanner: (householdName: string) =>
      `Du er invitert til ${householdName}. Etter at du har opprettet konto blir invitasjonen automatisk lagt klar til deg.`,
    acceptanceIntro: "Jeg har lest og godtar",
    acceptanceTermsLink: "vilkårene",
    acceptanceConnector: "og",
    acceptanceprivacyLink: "personvernerklæringen",
    acceptanceRequired:
      "Du må godta vilkårene og personvern­erklæringen før du kan registrere deg.",
    successTitle: "Konto opprettet! ✨",
    successDescription:
      "Vi har sendt en bekreftelseslenke til e-posten din. Du kan likevel bruke Fellesen med en gang.",
  },

  forgotPassword: {
    title: "Glemt passord",
    description:
      "Skriv inn e-postadressen din, så sender vi deg en lenke for å sette et nytt passord.",
    emailLabel: "E-post",
    submitButton: "Send lenke",
    submitting: "Sender…",
    successTitle: "Lenke sendt",
    successDescription:
      "Hvis e-postadressen er knyttet til en konto, har vi sendt en lenke for å tilbakestille passordet. Lenken er gyldig i én time.",
  },

  resetPassword: {
    title: "Velg nytt passord",
    description: "Lenken kan kun brukes én gang. Etter dette må du logge inn på nytt.",
    invalidLink:
      "Lenken er ugyldig eller utløpt. Be om en ny via «Glemt passord» på innloggings­siden.",
    passwordLabel: "Nytt passord",
    passwordHint: "Minst 10 tegn.",
    passwordConfirmLabel: "Gjenta passord",
    passwordMismatchError: "Passordene må være like.",
    submitButton: "Lagre nytt passord",
    submitting: "Lagrer…",
    successTitle: "Passordet er oppdatert",
    successDescription:
      "Du blir nå sendt tilbake til innloggingen. Bruk det nye passordet for å logge inn.",
  },

  verifyEmail: {
    title: "Bekrefter e-posten din…",
    verifyingDescription: "Litt tålmodighet — vi sjekker lenken.",
    successTitle: "E-posten er bekreftet 🎉",
    successDescription: "Takk! Du kan lukke fanen, eller fortsette der du var.",
    failureTitle: "Kunne ikke bekrefte e-posten",
    failureDescription: (message: string) => `Detaljer: ${message}`,
    requestNewLink: "Be om en ny lenke",
    continueButton: "Fortsett til Fellesen",
  },

  connection: {
    // Vises som toast når WebSocket-tilkoblingen mister bakken og hooken
    // går i reconnect-modus. Forsvinner automatisk når status flipper
    // tilbake til "connected".
    reconnectingTitle: "Mister tilkobling",
    reconnectingDescription: "Prøver å koble til på nytt …",
    reconnectingDismissLabel: "Lukk",
    // Bekreftelse på at vi er online igjen. Kort varighet — bare ment
    // som signal om at evt. nylige endringer fra andre medlemmer nå
    // synes hos deg.
    reconnectedTitle: "Tilkoblet igjen",
    reconnectedDescription: "Vi er live igjen.",
    reconnectedDismissLabel: "Lukk",
    // Vises når nettleseren forteller oss at brukeren er offline.
    offlineTitle: "Ingen nettverkstilkobling",
    offlineDescription: "Vi venter til du er tilbake online.",
    offlineDismissLabel: "Lukk",
  },

  appShell: {
    myHouseholdsLabel: "Mine husstander",
    allHouseholds: "Alle husstander",
    logout: "Logg ut",
    memberCount: (n: number) => `${n} medlemmer`,
    userMenu: {
      signedInAs: "Pålogget som",
      profileItem: "Profil",
      myCardsItem: "Mine kort",
      settingsItem: "Innstillinger",
    },
    settingsDialog: {
      title: "Innstillinger",
      description: "Velg utseende og valuta. Lagres lokalt i nettleseren.",
      themeLabel: "Tema",
      themeOptions: {
        light: "Lys",
        dark: "Mørk",
        system: "Følg system",
      },
      currencyLabel: "Valuta",
      currencyHint: "Foreløpig kun norske kroner.",
    },
    profileDialog: {
      title: "Min profil",
      description: "Endre hvordan navnet ditt vises for andre medlemmer.",
      displayNameLabel: "Visningsnavn",
      displayNamePlaceholder: "F.eks. Lisa",
      emailLabel: "E-post",
      emailHint: "E-posten er knyttet til innloggingen din og kan ikke endres her.",
      saveFailed: (message: string) => `Kunne ikke lagre: ${message}`,
      verification: {
        verifiedTitle: "E-posten er bekreftet",
        verifiedDescription: "Du kan opprette husstander og oppgjør, og akseptere invitasjoner.",
        unverifiedTitle: "Ikke bekreftet ennå",
        unverifiedDescription:
          "Bekreft e-posten din for å kunne opprette husstander og oppgjør, og akseptere invitasjoner.",
        resendButton: "Send bekreftelsesmail på nytt",
        resendSentToast: {
          title: "Bekreftelsesmail sendt",
          description: "Sjekk innboksen — også søppelpost hvis den ikke har dukket opp.",
          dismissLabel: "Lukk",
        },
        resendFailedToast: {
          title: "Kunne ikke sende mail",
          dismissLabel: "Lukk",
        },
      },
      deleteAccount: {
        sectionTitle: "Slett kontoen",
        sectionDescription:
          "Sletter brukeren din og alt knyttet til den. Husstander hvor du er eneste medlem blir slettet og dataen blir uleselig. E-postadressen din frigis så du kan registrere deg på nytt senere.",
        button: "Slett konto",
        confirmTitle: "Bekreft sletting",
        confirmDescription:
          "Dette kan ikke angres. Skriv inn e-postadressen din for å bekrefte at du virkelig vil slette kontoen.",
        confirmEmailLabel: "E-postadresse",
        confirmEmailPlaceholder: "Skriv inn e-posten din",
        confirmEmailMismatch: "E-postadressen stemmer ikke.",
        cancelButton: "Avbryt",
        deleteButton: "Slett kontoen min",
        successToast: {
          title: "Kontoen er slettet",
          description: "Du er nå logget ut. E-postadressen din er frigjort for ny registrering.",
          dismissLabel: "Lukk",
        },
        blockedTitle: "Kan ikke slettes",
        blockedDescription:
          "Du er eneste eier av en husstand med andre medlemmer. Overfør eierskap eller fjern medlemmene først.",
        failedTitle: "Kunne ikke slette",
      },
    },
  },

  households: {
    title: "Husstander",
    description: "Hver husstand kan ha flere medlemmer og oppgjør.",
    newHousehold: "Ny husstand",
    loadFailed: (message: string) => `Kunne ikke laste husstander: ${message}`,
    emptyTitle: "Ingen husstander ennå",
    emptyDescription: "Opprett din første husstand for å begynne å dele fakturaer med andre.",
    emptyAction: "Opprett husstand",
    createdAt: (relative: string) => `Opprettet ${relative}`,
    memberCount: (n: number) => `${n} medlemmer`,
    activeSettlements: (n: number) => `${n} aktive oppgjør`,
    searchPlaceholder: "Søk etter husstand",
    searchLabel: "Søk etter husstand",
    searchEmpty: (query: string) => `Ingen treff på «${query}».`,
    favoriteAddLabel: "Legg til som favoritt",
    favoriteRemoveLabel: "Fjern som favoritt",
    favoriteBadge: "Favoritt",
    createDialog: {
      title: "Ny husstand",
      description: "Gi husstanden et navn — du kan endre det senere.",
      nameLabel: "Navn",
      namePlaceholder: "F.eks. Felles husstand",
    },
  },

  invitations: {
    list: {
      heading: (n: number) =>
        n === 1 ? "Du har 1 ventende invitasjon" : `Du har ${n} ventende invitasjoner`,
      pendingBadge: "Invitasjon",
      invitedBy: (name: string) => `Invitert av ${name}`,
      invitedAnonymous: "Du har blitt invitert til å bli medlem",
      accept: "Godta",
      decline: "Avslå",
      acceptedToast: "Du er nå medlem av husstanden",
      declinedToast: "Invitasjonen er avslått",
      unverifiedDialog: {
        title: "Bekreft e-posten din først",
        description:
          "Vi sendte en bekreftelseslenke da du registrerte deg. Du må klikke den før du kan akseptere invitasjoner. Sjekk innboksen — også søppelpost.",
        emailLine: (email: string) => `Sjekk innboksen for ${email}.`,
        resendButton: "Send bekreftelsesmail på nytt",
        closeButton: "Lukk",
        resentToast: "Bekreftelsesmail sendt",
        resendFailedToast: "Kunne ikke sende mail",
      },
    },
    toast: {
      titleSingle: (householdName: string) => `Du har blitt invitert til «${householdName}»`,
      titleMultiple: (n: number) => `Du har ${n} nye invitasjoner`,
      descriptionFrom: (name: string) => `Fra ${name}. Åpne husstander for å svare.`,
      descriptionMultiple: "Åpne husstander for å se og svare.",
      viewAction: "Se invitasjoner",
      dismissLabel: "Lukk varsel",
    },
  },

  household: {
    loadFailed: "Kunne ikke laste husstand",
    accessRevokedToast: "Du har blitt fjernet fra husstanden",
    headerDescription: (memberCount: number, role: "owner" | "member") =>
      `${memberCount} medlemmer · ${role === "owner" ? "Eier" : "Medlem"}`,
    tabs: {
      settlements: "Oppgjør",
      members: "Medlemmer",
      categories: "Kategorier",
      settings: "Innstillinger",
    },
    settlements: {
      countHeading: (n: number) => `${n} oppgjør`,
      newSettlement: "Nytt oppgjør",
      emptyTitle: "Ingen oppgjør ennå",
      emptyDescription: "Opprett ditt første oppgjør for å begynne å dele utgifter.",
      emptyAction: "Opprett oppgjør",
      invoiceCount: (n: number) => `${n} fakturaer`,
      expenseCount: (n: number) => `${n} utgifter`,
      yearSummary: (count: number, totalLabel: string) => `${count} oppgjør · ${totalLabel}`,
      searchPlaceholder: "Søk etter oppgjør",
      searchLabel: "Søk etter oppgjør",
      searchEmpty: "Ingen oppgjør matcher filtrene.",
      filtersButton: "Filter",
      filtersClear: "Tøm filter",
      createDialog: {
        title: "Nytt oppgjør",
        description: "Et oppgjør samler fakturaer og utgifter for en periode.",
        titleLabel: "Tittel",
        titlePlaceholder: "F.eks. Mai 2026",
        startDateLabel: "Startdato",
        startDateFutureMonthError: "Kan ikke være i en fremtidig måned",
        endDateLabel: "Forfallsdato",
        endDateAfterStartError: "Må være etter startdato",
      },
    },
    members: {
      countHeading: (n: number) => `${n} medlemmer`,
      invite: "Inviter",
      removeFromHousehold: "Fjern fra husstanden",
      removingOverlay: "Fjerner medlem…",
      removeFailedToast: "Kunne ikke fjerne medlem",
      inviteDialog: {
        title: "Inviter til husstanden",
        description: "Personen får en e-post med en lenke for å bli med.",
        emailLabel: "E-post",
        emailPlaceholder: "navn@example.com",
        submit: "Send invitasjon",
      },
      removeDialog: {
        title: "Fjern medlem",
        description: (name: string) =>
          `Er du sikker på at du vil fjerne ${name} fra husstanden? Personen mister tilgang umiddelbart.`,
        submit: "Fjern medlem",
      },
      pendingInvitations: {
        heading: (n: number) => (n === 1 ? "1 ventende invitasjon" : `${n} ventende invitasjoner`),
        statusBadge: "Venter",
        invitedAt: (relative: string) => `Invitert ${relative}`,
        cancel: "Trekk tilbake",
        cancellingOverlay: "Trekker tilbake invitasjon…",
        cancelFailedToast: "Kunne ikke trekke tilbake invitasjon",
      },
      revokedNotifications: {
        title: "Invitasjon kansellert",
        description: (email: string) =>
          `${email} slettet kontoen sin. Invitasjonen er derfor fjernet.`,
      },
    },
    categories: {
      countHeading: (n: number) => `${n} kategorier`,
      description: "Utgifter blir automatisk plassert basert på kategori-innstillinger.",
      newCategory: "Ny kategori",
      searchPlaceholder: "Søk i kategorier…",
      searchEmpty: (query: string) =>
        query ? `Ingen kategorier matcher «${query}».` : "Ingen kategorier å vise.",
      patternSummary: (patterns: number, matches: number) =>
        `${patterns} mønstre · ${matches} treff`,
      noPatterns: "Ingen automatiske mønstre",
      addPatternPlaceholder: "Legg til mønster…",
      firstPatternPlaceholder: "F.eks. REMA 1000 — trykk Enter",
      addPatternButton: "Legg til",
      patternHint:
        "Skriv et nøkkelord og trykk Enter. Store/små bokstaver ignoreres — «REMA» matcher også «rema 1000 oslo».",
      removePatternLabel: (pattern: string) => `Fjern mønster ${pattern}`,
      matchCountSuffix: (matches: number) => `${matches} treff`,
      sharedLabel: "Fellesutgift",
      defaultBadge: "Standard",
      updateFailedToast: "Kunne ikke oppdatere kategori",
      deletingOverlay: "Sletter kategori…",
      deleteFailedToast: "Kunne ikke slette kategori",
      createDialog: {
        title: "Ny kategori",
        description: "Legg til en egen kategori for husstanden.",
        nameLabel: "Navn",
        namePlaceholder: "F.eks. Barnehage",
        iconLabel: "Ikon",
        colorLabel: "Farge",
        sharedLabel: "Fellesutgift som standard",
        sharedHint: "Nye utgifter i denne kategorien deles likt mellom medlemmene.",
        patternsLabel: "Automatiske mønstre",
        patternsHint: "Komma-separert. Treffer mot transaksjonsbeskrivelse.",
        patternsPlaceholder: "F.eks. BARNEHAGE, SFO",
      },
      editDialog: {
        title: "Rediger kategori",
        description: "Endre navn, farge, ikon og mønstre.",
      },
      deleteDialog: {
        title: "Slett kategori",
        description: (name: string) =>
          `Er du sikker på at du vil slette «${name}»? Utgifter som er knyttet til kategorien blir ukategoriserte.`,
        submit: "Slett kategori",
      },
    },
    settings: {
      nameSection: {
        heading: "Navn på husstand",
        hint: "Brukes i lister og notifikasjoner.",
      },
      ownerCoversInvoice: {
        heading: "Eier dekker hele fakturaen",
        hint: "Husstandens eier betaler kortfakturaen i sin helhet. Da skylder alle andre medlemmer eieren — sin andel av fellesutgifter og hele beløpet for sine private utgifter.",
        memberHint: "Bare eier kan endre denne innstillingen.",
      },
      connectedCards: {
        heading: "Tilkoblede kort",
        hint: "Disse kortene mater transaksjoner inn i husstandens oppgjør. Bare eieren av et kort kan endre om det deles med husstanden — gjøres fra «Mine kort» i profilen.",
        empty:
          "Ingen kort er delt med denne husstanden enda. Be eieren dele et kort fra «Mine kort».",
        sharedBy: (owner: string) => `Delt av ${owner}`,
        sharedByYou: "Delt av deg",
        manageLink: "Administrer i Mine kort",
        loadFailed: (msg: string) => `Klarte ikke å hente delte kort: ${msg}`,
      },
      dangerZone: {
        heading: "Slett husstand",
        hint: "Permanent — kan ikke angres. Alle oppgjør og utgifter slettes også.",
        deleteButton: "Slett husstand",
        deleteDialog: {
          title: "Slett husstand",
          description: (name: string) =>
            `Er du sikker på at du vil slette «${name}»? Alle medlemmer, oppgjør, fakturaer og utgifter blir borte. Dette kan ikke angres.`,
          submit: "Slett husstand",
        },
      },
    },
  },

  settlement: {
    loadFailed: "Kunne ikke laste oppgjør",
    actions: {
      edit: "Rediger oppgjør",
      delete: "Slett oppgjør",
    },
    editDialog: {
      title: "Rediger oppgjør",
      description: "Endre tittel og periode for oppgjøret.",
      titleLabel: "Tittel",
      titlePlaceholder: "F.eks. Mai 2026",
      startDateLabel: "Startdato",
      endDateLabel: "Forfallsdato",
      endDateAfterStartError: "Må være etter startdato",
    },
    deleteDialog: {
      title: "Slett oppgjør",
      description: (title: string) =>
        `Er du sikker på at du vil slette «${title}»? Alle fakturaer, utgifter og godkjenninger blir borte. Dette kan ikke angres.`,
      submit: "Slett oppgjør",
    },
    invoices: {
      heading: "Fakturaer",
      upload: "Last opp",
      empty: "Ingen fakturaer ennå. Last opp en PDF for å begynne.",
      fromInvoice: (filename: string) => `Fra ${filename}`,
      transactionCount: (n: number) => `${n} utgifter`,
      addedBadge: "Lagt til i tabellen",
      deletingOverlay: "Sletter faktura…",
      deleteFailedToast: "Kunne ikke slette faktura",
      filenameTooltip: (filename: string) => `Filnavn: ${filename}`,
      rowActions: {
        viewExpenses: "Se utgifter",
        delete: "Slett faktura",
      },
      uploadDialog: {
        title: "Last opp faktura",
        description: "Transaksjoner blir hentet ut og lagt til i oppgjøret automatisk.",
        dropHere: "Slipp PDF her",
        clickToSelect: "eller klikk for å velge fil",
        longPressHint: "Hold inne for å lime inn",
        chooseAnother: "Velg en annen fil",
        submitIdle: "Last opp",
        submitProcessing: "Behandler…",
        errorNotPdf: "Filen må være en PDF.",
        errorTooLarge: "PDF-en er større enn 50 MB.",
        pasteNoPdf: "Ingen PDF i utklippstavlen.",
        pasteFailed: "Kunne ikke lese utklippstavlen.",
      },
      resolvePayerDialog: {
        title: "Tilordne betaler",
        description: (n: number, names: string[]) => {
          const namesText = names.length > 0 ? ` (${names.join(", ")})` : "";
          return n === 1
            ? `1 transaksjon har et kortholdernavn${namesText} som ikke finnes blant medlemmene i oppgjøret. Velg hvem som dekker den.`
            : `${n} transaksjoner har kortholdernavn${namesText} som ikke finnes blant medlemmene i oppgjøret. Velg hvem som dekker dem.`;
        },
        paidByLabel: "Tilordne til medlem",
        submit: "Lagre tilordning",
        skip: "Hopp over",
        errorNoMembers: "Husstanden har ingen medlemmer å velge mellom.",
      },
      issuesDialog: {
        title: "Mulig avvik på fakturaen",
        description:
          "Fakturaen ble lastet opp og transaksjonene er lagt til, men ekstraksjonen oppdaget noe du bør sjekke før oppgjøret avsluttes.",
        acknowledge: "Forstått, lukk",
        sumMismatch:
          "Summen av transaksjonene matcher ikke totalbeløpet oppgitt på fakturaen. Det kan bety at en eller flere transaksjoner mangler — sjekk fakturaen manuelt for å være sikker på at alt kom med.",
        totalAmountDisagreement:
          "Ekstraksjonen er usikker på fakturaens totalsum (de ulike pipelinene var ikke enige). Sjekk transaksjonene mot fakturaen før oppgjøret avsluttes.",
        fallback:
          "Ekstraksjonen flagget en mulig uregelmessighet på fakturaen. Vi anbefaler at du sjekker transaksjonene manuelt.",
        detailsLabel: "Tekniske detaljer",
      },
      viewDialog: {
        title: (filename: string) => `Utgifter fra ${filename}`,
        description: (n: number) =>
          n === 1
            ? "1 utgift hentet ut og lagt til i tabellen."
            : `${n} utgifter hentet ut og lagt til i tabellen.`,
        empty: "Ingen utgifter er knyttet til denne fakturaen.",
        totalLabel: "Sum",
      },
      deleteDialog: {
        title: "Slett faktura",
        description: (filename: string, n: number) =>
          n === 0
            ? `Er du sikker på at du vil slette «${filename}»? Dette kan ikke angres.`
            : n === 1
              ? `Er du sikker på at du vil slette «${filename}»? 1 utgift som ble hentet ut blir også slettet. Dette kan ikke angres.`
              : `Er du sikker på at du vil slette «${filename}»? ${n} utgifter som ble hentet ut blir også slettet. Dette kan ikke angres.`,
        submit: "Slett faktura",
      },
    },
    expenses: {
      heading: "Utgifter",
      addButton: "Legg til",
      deletingOverlay: "Sletter utgift…",
      deleteFailedToast: "Kunne ikke slette utgift",
      collapseLabel: "Skjul utgifter",
      expandLabel: "Vis utgifter",
      summary: (count: number, total: string) =>
        count === 1 ? `1 utgift · ${total}` : `${count} utgifter · ${total}`,
      filters: {
        all: "Alle",
        shared: "Felles",
        private: "Privat",
        manual: "Manuelle",
        fromInvoice: "Fra faktura",
        live: "Fra kort",
        typeHeading: "Type",
        sourceHeading: "Kilde",
      },
      cardSourceChip: (aspsp: string, mask: string | null) => (mask ? `${aspsp} ··${mask}` : aspsp),
      cardSourceChipUnknown: "Fra kort",
      // "Fra: [ikon] [label]"-prefiks brukt på alle kilde-chips i
      // utgiftstabellen (både live-kortrader og fakturarader).
      sourcePrefix: "Fra:",
      sourceFromInvoice: (filename: string) => filename,
      filtersButton: "Filter",
      filtersClear: "Tøm filter",
      emptyTitle: "Ingen utgifter",
      emptyDescriptionAll: "Last opp en faktura eller legg til utgifter manuelt.",
      emptyDescriptionFiltered: "Ingen utgifter matcher filteret.",
      layoutToggle: {
        groupLabel: "Visningstype",
        card: "Kort",
        table: "Tabell",
      },
      searchPlaceholder: "Søk etter utgift",
      searchLabel: "Søk etter utgift",
      descriptionPopover: {
        copy: "Kopier",
        copied: "Kopiert",
      },
      cardDetails: {
        infoLabel: "Mer informasjon",
        sourceLabel: "Kilde",
        sourceManual: "Lagt til manuelt",
        sourceInvoice: "Fra faktura",
        sourceLive: "Fra koblet kort",
        invoiceLabel: "Faktura",
        cardholderLabel: "Kortholder",
        cardholderUnknown: "Ukjent",
        splitLabel: "Fordeling",
      },
      table: {
        rowNumber: "Nr.",
        date: "Dato",
        description: "Beskrivelse",
        category: "Kategori",
        paidBy: "Betalt av",
        type: "Fellesutgift",
        amount: "Beløp",
        uncategorized: "Ukategorisert",
        manualBadge: "Manuell",
        editedBadge: "Endret",
        customSplitBadge: "Tilpasset fordeling",
        payerUnresolvedBadge: "Trenger gjennomgang",
        payerUnresolvedTooltip: (cardholderName: string | null | undefined) =>
          cardholderName
            ? `Kortholdernavnet «${cardholderName}» fra fakturaen finnes ikke som medlem av oppgjøret.`
            : "Kortholderen fra fakturaen finnes ikke som medlem av oppgjøret.",
        sortAsc: (column: string) =>
          `Sortert stigende på ${column.toLowerCase()}. Klikk for å sortere synkende.`,
        sortDesc: (column: string) =>
          `Sortert synkende på ${column.toLowerCase()}. Klikk for å sortere stigende.`,
        sortNone: (column: string) => `Klikk for å sortere på ${column.toLowerCase()}.`,
        rowActions: {
          edit: "Rediger",
          adjustSplit: "Juster fordeling",
          delete: "Slett",
        },
      },
      addDialog: {
        title: "Legg til utgift",
        description: "Manuell registrering — markeres tydelig som ikke-automatisk.",
        descriptionLabel: "Beskrivelse",
        descriptionPlaceholder: "F.eks. Middag på Mathallen",
        dateLabel: "Dato",
        amountLabel: "Beløp",
        amountPlaceholder: "0.00",
        amountSuffix: "kr",
        categoryLabel: "Kategori",
        noCategory: "Ingen kategori",
        categoryOptionSuffix: (defaultShared: boolean) => (defaultShared ? "(felles)" : "(privat)"),
        paidByLabel: "Betalt av",
        sharedSwitchLabel: "Fellesutgift",
        sharedHintShared: "Deles likt mellom medlemmer",
        sharedHintPrivate: "Du dekker selv",
        submit: "Legg til",
      },
      editDialog: {
        title: "Rediger utgift",
        description: "Endringer markeres som manuelt redigert.",
      },
      splitDialog: {
        title: "Juster fordeling",
        description:
          "Bestem hvem som skal være med på fordelingen, og hvor mye hver enkelt dekker. Du kan også flytte utgiften til en annen betaler.",
        paidByLabel: "Betalt av",
        totalLabel: "Sum å fordele",
        modeGroupLabel: "Inntastingsmodus",
        modeAmount: "kr",
        modePercent: "%",
        percentSuffix: "%",
        allocatedLabel: "Fordelt",
        remainingLabel: "Gjenstår",
        shareSuffix: "kr",
        includeLabel: "Inkluder",
        presetsHeading: "Hurtigvalg",
        equalSplitAll: "Del likt på alle",
        equalSplitIncluded: "Del likt på valgte",
        onlyPayerCovers: "Bare betaleren dekker",
        onlyMemberCovers: (name: string) => `Bare ${name} betaler sin del`,
        payerCoversRest: "Betaleren dekker resten",
        split7030: "70 / 30",
        split6040: "60 / 40",
        skewedRequiresTwo: "Krever nøyaktig to inkluderte medlemmer.",
        clearShares: "Nullstill andeler",
        submit: "Lagre fordeling",
        mismatchError: (diff: string) => `Fordelingen må stemme med beløpet (${diff} kr unna).`,
        mismatchErrorPercent: (diff: string) =>
          `Fordelingen må stemme med beløpet (${diff} % unna).`,
        emptyError: "Minst én person må være med på fordelingen.",
      },
      deleteDialog: {
        title: "Slett utgift",
        description: (description: string) =>
          `Er du sikker på at du vil slette «${description}»? Dette kan ikke angres.`,
        submit: "Slett utgift",
      },
      smartCategorize: {
        bulk: {
          title: "Sett samme kategori på like utgifter?",
          body: (n: number, description: string, categoryName: string) =>
            `Det finnes ${n} ${n === 1 ? "annen utgift" : "andre utgifter"} med beskrivelsen «${description}». Vil du sette ${n === 1 ? "den" : "alle"} til «${categoryName}»?`,
          listHeading: "Påvirkede utgifter",
          listMoreCount: (n: number) => `+${n} flere`,
          savePatternLabel: (categoryName: string) => `Lagre mønster på «${categoryName}»`,
          savePatternHint:
            "Mønsteret brukes til å auto-kategorisere lignende utgifter ved fremtidige opplastninger.",
          patternTip:
            "Kortere mønster fanger flere utgifter — f.eks. «FLYBUSSEN» treffer både Oslo og Bodø. Lengre mønster er mer presist.",
          viewCategoryLink: "Administrer mønstre på kategorien",
          submit: "Ja, oppdater",
          cancel: "Nei takk",
          successToast: (n: number) =>
            n === 1 ? "1 utgift fikk ny kategori." : `${n} utgifter fikk ny kategori.`,
          successWithPatternToast: (n: number) =>
            n === 1
              ? "1 utgift oppdatert og mønster lagret."
              : `${n} utgifter oppdatert og mønster lagret.`,
          patternErrorToast: "Utgiftene ble oppdatert, men mønsteret kunne ikke lagres.",
          errorToast: "Kunne ikke oppdatere utgiftene. Prøv igjen.",
        },
        pattern: {
          title: "Lagre som mønster på kategorien?",
          intro: (categoryName: string) =>
            `Du har nå satt to utgifter med liknende navn til «${categoryName}». Vil du lagre et mønster slik at fremtidige opplastninger automatisk havner i samme kategori?`,
          guidance:
            "Et mønster er en del av et utgiftsnavn som gjenkjennes ved opplastning. Hold det kort og spesifikt — f.eks. «REMA 1000» for alle REMA-butikker, eller «RUTER» for kollektivreiser.",
          inputLabel: "Mønster",
          inputPlaceholder: "Skriv et mønster…",
          suggestionsLabel: "Forslag",
          suggestionsHint: "Klikk for å bruke",
          previewMatchesNone: "Ingen utgifter i dette oppgjøret matcher mønsteret ennå.",
          previewMatchesSome: (n: number) =>
            n === 1
              ? "Matcher 1 utgift i dette oppgjøret."
              : `Matcher ${n} utgifter i dette oppgjøret.`,
          duplicateNote: "Mønsteret finnes allerede på denne kategorien.",
          submit: "Lagre mønster",
          cancel: "Ikke nå",
          successToast: "Mønster lagt til på kategorien.",
          errorToast: "Kunne ikke lagre mønsteret. Prøv igjen.",
        },
      },
    },
    charts: {
      sharedByCategoryTitle: "Utgifter etter kategori",
      sharedByCategoryEmpty: "Ingen utgifter ennå.",
      sharedByCategoryCenterHint: "kr totalt",
      sharedCountHint: (n: number) => (n === 1 ? "1 utgift" : `${n} utgifter`),
      topCategoryHint: (label: string, share: string) => `Størst kategori: ${label} (${share})`,
      uncategorized: "Ukategorisert",
      perMemberTitle: "Hvem har lagt ut hva",
      perMemberEmpty: "Ingen utgifter ennå.",
      perMemberFootnote:
        "Stolpen er delt i private utgifter og fellesutgifter. Bare fellesutgifter inngår i oppgjøret — private utgifter dekker hvert medlem selv.",
      breakdownPaidShared: (amount: string) => `fellesutgifter ${amount}`,
      breakdownPrivate: (amount: string) => `private utgifter ${amount}`,
      memberFilterLabel: "Vis utgifter for",
      memberFilterAll: "Alle medlemmer",
      memberFilterSharedOnly: "Kun fellesutgifter",
      memberFilterPersonalHint: (name: string) =>
        `Viser utgifter ${name} har lagt ut i oppgjøret — felles og private, fulle beløp.`,
      memberFilterAllHint: "Viser alle utgifter — felles og private",
      memberFilterSharedHint: "Viser kun fellesutgifter",
    },
    summary: {
      heading: "Oppgjør",
      noTransfers: "Ingen overføringer nødvendig — alle har bidratt likt.",
      transfersHeading: "Overføringer",
      transferCountHint: (n: number) => (n === 1 ? "1 overføring" : `${n} overføringer`),
      yourPositionLabel: "Din posisjon",
      yourPositionReceives: (amount: string) => `Du skal få ${amount}`,
      yourPositionOwes: (amount: string) => `Du skylder ${amount}`,
      yourPositionEven: "Du er kvitt",
      transferVerb: "skylder",
      copyTransferAmountAriaLabel: (amount: string) => `Kopier ${amount}`,
      copyTransferAmountCopiedAriaLabel: "Beløp kopiert",
      approvalsHeading: (approved: number, total: number) => `Godkjenninger (${approved}/${total})`,
      approvedAt: (date: string) => `Godkjent ${date}`,
      youSuffix: " (deg)",
      pending: "Venter",
      approveButton: "Godkjenn",
      undoApproveButton: "Angre",
      allApprovedHint: "Alle har godkjent. Du kan gjøre opp oppgjøret nå.",
      lockBeforeAllApprovedHint: (approved: number, total: number) =>
        `Bare ${approved} av ${total} medlemmer har godkjent. Som eier kan du likevel gjøre opp.`,
      waitingForOwnerHint: "Venter på at eier gjør opp oppgjøret.",
      lockButton: "Gjør opp",
      lockedAt: (date: string) => `Gjort opp ${date}.`,
      lockedAtWithoutAllApproved: (date: string, approved: number, total: number) =>
        `Gjort opp ${date} — kun ${approved} av ${total} medlemmer hadde godkjent.`,
      progressTitle: (approved: number, total: number) =>
        approved === 0
          ? "Klar til å starte oppgjøret"
          : approved === total
            ? "Klar til å gjøre opp! 🎉"
            : `${approved} av ${total} har godkjent`,
      progressTitleLocked: "Oppgjøret er gjort opp ✅",
      progressHintNone: "Inviter medlemmene til å godkjenne sin del.",
      progressHintPartial: (remaining: number) =>
        remaining === 1
          ? "Venter på 1 person til. Send et vennlig dytt!"
          : `Venter på ${remaining} til. Send en påminnelse hvis det haster.`,
      progressHintAll: "Alle har godkjent. Trykk «Gjør opp» når dere er klare.",
      progressHintLocked: (date: string) =>
        `Gjort opp ${date}. Alle overføringer er gjort — denne perioden er ferdig.`,
      youApprovedCelebrate: "Takk for godkjenningen!",
      celebrateAllApproved: "Alle har sagt sitt — på tide å gjøre opp!",
      notifyButton: "Send påminnelse",
      notifyButtonShort: "Minn på",
      notifyDialog: {
        title: "Send påminnelse om oppgjøret",
        description:
          "Vi sender en e-post til alle medlemmer som ennå ikke har godkjent, slik at de kan gå gjennom sin del.",
        recipientsHeading: "Mottakere",
        emptyRecipients: "Alle medlemmer har allerede godkjent — ingen å varsle.",
        submit: "Send e-post",
        sending: "Sender…",
        successTitle: "Påminnelse sendt",
        successDescription: (n: number) =>
          n === 1 ? "1 medlem fikk en e-post." : `${n} medlemmer fikk en e-post.`,
        partialFailureTitle: "Påminnelse delvis sendt",
        partialFailureDescription: (sent: number, failed: number) =>
          `${sent} medlem${sent === 1 ? "" : "mer"} fikk e-post, men ${failed} feilet. Prøv igjen om litt.`,
        skippedTitle: "Varsler er skrudd av",
        skippedDescription:
          "Påminnelsen ble ikke sendt fordi NOTIFICATIONS_ENABLED står til «false» på serveren. Slå den på i .env for å sende ekte e-poster.",
        failureTitle: "Kunne ikke sende påminnelse",
        failureDescription: (message: string) => `Feil: ${message}`,
      },
      lockConfirmDialog: {
        title: "Gjør opp oppgjøret?",
        descriptionAllApproved:
          "Alle medlemmer har godkjent. Når du gjør opp oppgjøret blir det låst og kan ikke endres etterpå.",
        descriptionNotAllApproved: (approved: number, total: number) =>
          `Bare ${approved} av ${total} medlemmer har godkjent. Når du gjør opp oppgjøret blir det låst og kan ikke endres etterpå.`,
        warning: "Å gjøre opp et oppgjør er endelig — handlingen kan ikke angres.",
        submit: "Gjør opp",
      },
      reopenButton: "Gjenåpne oppgjør",
      reopenConfirmDialog: {
        title: "Gjenåpne oppgjøret?",
        description:
          "Som eier kan du gjenåpne et oppgjør om noe må rettes opp. Alle medlemmer ser i historikken at oppgjøret ble gjenåpnet.",
        warning:
          "Alle godkjenninger nullstilles, og medlemmene må godkjenne på nytt før oppgjøret kan låses igjen.",
        submit: "Gjenåpne",
      },
      reopenSuccessToast: "Oppgjøret er gjenåpnet. Alle godkjenninger er nullstilt.",
      reopenFailureToast: (message: string) => `Kunne ikke gjenåpne oppgjøret: ${message}`,
      history: {
        heading: "Historikk",
        empty: "Ingen hendelser registrert ennå.",
        loading: "Henter historikk…",
        unknownActor: "Ukjent medlem",
        you: "Du",
        // Approve / unapprove har separate selv- og andre-former fordi
        // refleksivt "sin" kun kan vise tilbake til et tredjepersons-
        // subjekt. "Du godkjente sin del" er feil norsk — det skal være
        // "Du godkjente din del". De andre verbene tar ikke refleksiv
        // possessiv, så samme streng fungerer for begge subjekttypene.
        created: (actor: string) => `${actor} opprettet oppgjøret`,
        approvedSelf: "Du godkjente din del",
        approvedOther: (actor: string) => `${actor} godkjente sin del`,
        unapprovedSelf: "Du trakk tilbake din godkjenning",
        unapprovedOther: (actor: string) => `${actor} trakk tilbake sin godkjenning`,
        locked: (actor: string) => `${actor} gjorde opp oppgjøret`,
        // Passiv form for oppgjør som ble låst før historikk-featuren kom
        // i prod — vi har låsetidspunktet i `settlement.locked_at`, men
        // ingen registrering av hvem som faktisk gjorde det opp.
        lockedUnknownActor: "Oppgjøret ble gjort opp",
        reopened: (actor: string) => `${actor} gjenåpnet oppgjøret`,
      },
    },
  },

  settlementStatus: {
    locked: "Gjort opp",
    overdue: "Forfalt",
    awaitingApproval: "Venter på godkjenning",
    open: "Åpen",
    dueToday: "Forfaller i dag",
    dueTomorrow: "Forfaller i morgen",
    dueInDays: (n: number) => `Forfaller om ${n} dager`,
    overdueOneDay: "1 dag forfalt",
    overdueDays: (n: number) => `${n} dager forfalt`,
  },

  footer: {
    privacy: "Personvern",
    terms: "Vilkår",
    securityNote: "Felles oversikt. Felles oppgjør.",
  },

  terms: {
    pageTitle: "Brukervilkår",
    pageLead:
      "Disse vilkårene gjelder mellom deg og " +
      "KLOGT AS (org.nr. 935 950 945) " +
      "når du bruker Fellesen. Vi har skrevet dem så kort og rett-på " +
      "som vi kan — du skal kunne lese gjennom dem uten å være advokat.",
    lastUpdated: "Sist oppdatert: 24. mai 2026",

    sections: {
      acceptance: {
        title: "1. Aksept av vilkårene",
        body:
          "Ved å opprette en konto og bruke Fellesen aksepterer du disse " +
          "vilkårene og vår personvernerklæring. Hvis du ikke aksepterer " +
          "dem, kan du ikke bruke tjenesten. Vi kan endre vilkårene; " +
          "vesentlige endringer varsles via e-post minst 30 dager før " +
          "de trer i kraft.",
      },

      service: {
        title: "2. Hva Fellesen er",
        body:
          "Fellesen er en tjeneste for felles økonomi i en husholdning. " +
          "Du kan laste opp kort­fakturaer som PDF, og vi henter ut " +
          "transaksjonslinjene slik at du og resten av husstanden kan " +
          "fordele utgifter rettferdig. Tjenesten er i en tidlig fase " +
          "(beta) — vi jobber jevnlig på den, og funksjoner kan endres " +
          "underveis. Vi gir ikke noe servicenivå-løfte (SLA) i denne " +
          "fasen.",
      },

      account: {
        title: "3. Kontoen din",
        body: "Du er ansvarlig for å holde innlogginga di trygg. Det " + "betyr at du må:",
        bullets: [
          "Bruke et godt passord, eller logge inn med Google.",
          "Ikke dele kontoen din med andre. Hver person i husstanden " + "skal ha sin egen.",
          "Si fra til oss umiddelbart hvis du tror noen har fått " + "tilgang til kontoen din.",
        ],
      },

      acceptableUse: {
        title: "4. Hva du ikke kan bruke Fellesen til",
        body: "Når du bruker Fellesen, lover du å ikke:",
        bullets: [
          "Laste opp data som ikke tilhører deg eller husstanden din.",
          "Bruke tjenesten til ulovlige formål, eller på en måte som " +
            "krenker andres rettigheter.",
          "Forsøke å bryte sikkerheten, omgå tilgangs­kontroller, " +
            "eller hente ut data du ikke skal ha tilgang til.",
          "Misbruke tjenesten på måter som påvirker andre brukere — " +
            "for eksempel automatisert spamming, overdrevent mange " +
            "forespørsler, eller manipulasjon av andres data.",
          "Kopiere, dekompilere, eller forsøke å hente ut " + "kildekoden vår.",
        ],
        consequence:
          "Brudd kan føre til at vi suspenderer eller stenger kontoen " +
          "din. Ved alvorlige brudd kan vi også anmelde forholdet.",
      },

      yourContent: {
        title: "5. Innholdet du laster opp",
        body:
          "Du eier dataene dine. Vi tar ingen rettigheter til dem utover " +
          "det som trengs for å levere tjenesten:",
        bullets: [
          "Opplastede PDF-er slettes umiddelbart etter at vi har hentet " +
            "ut transaksjonslinjene (med en 24-timers oppryddings­buffer " +
            "som sikkerhetsnett).",
          "Vi kjører PDF-ene gjennom Cloudflare AI for å hente ut " +
            "transaksjonsdataene. Cloudflare trener ikke modellene sine " +
            "på dataene dine.",
          "Vi bruker dataene dine kun til å levere tjenesten — ikke til " +
            "markedsføring, analyse, eller videresalg.",
          "Strukturerte transaksjonsfelter (beskrivelse, beløp, dato) " +
            "lagres i databasen vår — beskrivelse og merchant er " +
            "kryptert. Se personvernerklæringen for detaljene.",
        ],
      },

      availability: {
        title: "6. Tilgjengelighet og endringer",
        body:
          "Vi gjør så godt vi kan for å holde Fellesen oppe, men vi " +
          "garanterer ingen oppetid i denne fasen. Vi kan også gjøre " +
          "endringer i tjenesten — legge til, endre, eller fjerne " +
          "funksjoner. Hvis vi gjør en endring som påvirker hvordan " +
          "du bruker tjenesten vesentlig, varsler vi deg på forhånd " +
          "via e-post.",
      },

      liability: {
        title: "7. Ansvarsbegrensning",
        body: "Fellesen leveres «som den er». Vi tar ikke ansvar for:",
        bullets: [
          "Tap eller skader som oppstår fordi tjenesten er nede, har " +
            "feil, eller ikke fungerer som forventet.",
          "Indirekte tap, følgeskader, eller tapt fortjeneste.",
          "Feil i transaksjons­ekstraksjon — du er selv ansvarlig for " +
            "å sjekke at tallene stemmer før du baserer økonomiske " +
            "beslutninger på dem.",
          "Tap som oppstår fordi du, eller noen andre i husstanden, " +
            "har fått tilgang til kontoen din.",
        ],
        cap:
          "Vårt maksimale ansvar overfor deg er begrenset til det " +
          "beløpet du har betalt for tjenesten de siste 12 månedene — " +
          "som per nå er null kroner siden tjenesten er gratis. " +
          "Disse begrensningene gjelder ikke for forsettlig eller " +
          "grovt uaktsomt mislighold fra vår side, eller for tap som " +
          "etter ufravikelig norsk forbruker­rett ikke kan begrenses.",
      },

      termination: {
        title: "8. Oppsigelse",
        body:
          "Du kan slette kontoen din når som helst fra " +
          "innstillingene. Vi kan suspendere eller stenge kontoen " +
          "din hvis du bryter disse vilkårene, eller hvis vi blir " +
          "pålagt det av norske myndigheter. Vi vil normalt varsle " +
          "deg først hvis situasjonen tillater det. Når kontoen " +
          "slettes, fjernes også krypteringsnøkkelen for husstanden " +
          "— se personvernerklæringen for hvordan dataene blir " +
          "uleselige etter dette.",
      },

      law: {
        title: "9. Norsk rett og verneting",
        body:
          "Disse vilkårene er underlagt norsk rett. Hvis vi får en " +
          "tvist vi ikke kan løse i fellesskap, skal saken behandles " +
          "ved Oslo tingrett — med mindre annet følger av ufravikelig " +
          "forbruker­vernlovgivning.",
      },

      contact: {
        title: "10. Kontakt",
        body: "Spørsmål om vilkårene? Skriv til ",
        contactEmail: "fellesen@klogt.no",
        copyEmailHint: "Klikk for å kopiere",
        copyEmailDone: "Kopiert!",
        contactAfter: ".",
      },
    },
  },

  privacy: {
    pageTitle: "Personvern",
    pageLead:
      "Vi vet at en kortfaktura sier mye om livet ditt — hvor du handler, " +
      "hvilke vaner du har, og hvem du støtter. Vi har bygget Fellesen slik " +
      "at de detaljene aldri kan leses av oss, av Cloudflare som driver " +
      "serverne, eller av databasen som lagrer dem.",
    lastUpdated: "Sist oppdatert: 24. mai 2026",

    sections: {
      controller: {
        title: "Hvem som er ansvarlig",
        // Tekst splittet i biter slik at orgnr og e-post kan rendres som
        // kopierbare verdier (CopyableValue) — samme idiom som
        // yourRights-seksjonen lenger ned.
        intro: "Fellesen leveres av KLOGT AS (org.nr. ",
        orgNr: "935 950 945",
        afterOrgNr:
          "). Vi er behandlings­ansvarlige for personopplysningene vi " +
          "lagrer om deg. Spørsmål om personvern, eller forespørsler om " +
          "innsyn/retting/sletting, sendes til ",
        contactEmail: "fellesen@klogt.no",
        outro: " — vi svarer innen 30 dager.",
        copyHint: "Klikk for å kopiere",
        copyDone: "Kopiert!",
      },

      legalBasis: {
        title: "Hvilket grunnlag vi behandler på",
        body:
          "Vi behandler personopplysninger på grunnlag av kontrakt " +
          "(GDPR Art. 6 nr. 1 bokstav b): for at Fellesen skal kunne " +
          "regne ut hvem som skylder hvem, må vi lagre fakturalinjene dine. " +
          "Når du oppretter en konto og legger til en utgift, inngår du " +
          "samtidig en avtale med oss om denne behandlingen. Du kan trekke " +
          "samtykket tilbake når som helst ved å slette kontoen din.",
      },

      retention: {
        title: "Hvor lenge vi lagrer",
        body:
          "Vi lagrer dataene dine så lenge kontoen din er aktiv. Når du " +
          "sletter kontoen sletter vi krypteringsnøkkelen for husstanden " +
          "din — etter det er fakturadetaljene matematisk uleselige, også " +
          "for oss. Vi rydder opp i de tomme radene innen 30 dager. " +
          "Opplastede PDF-er slettes umiddelbart etter ekstraksjon (med " +
          "en 24-timers lifecycle som ekstra sikkerhet). E-postlogger " +
          "(invitasjoner, varsler) holdes i 6 måneder for feilsøking.",
      },

      memberDeparture: {
        title: "Når du forlater en husstand",
        body:
          "Hvis du blir fjernet fra en husstand, eller velger å forlate " +
          "den selv, mister du umiddelbart tilgang til husstandens " +
          "oppgjør og utgifter. Navnet ditt blir stående i oppgjør hvor " +
          "du har lagt ut for noe, slik at gjenværende medlemmer fortsatt " +
          "kan forstå hvem som betalte hva. Hvis du vil slette navnet " +
          "ditt fullstendig, slett kontoen din — da fjernes alt som " +
          "knytter deg til de historiske oppgjørene.",
      },

      transfers: {
        title: "Overføring til land utenfor EØS",
        body:
          "Noen av tjenestene vi bruker driftes delvis fra USA. " +
          "Cloudflare (servere) og Resend (e-post) er amerikanske " +
          "selskaper. Vi har inngått standardkontraktsbestemmelser " +
          "(Standard Contractual Clauses) med dem, og begge er sertifisert " +
          "under EU-US Data Privacy Framework — det er Datatilsynets " +
          "anbefalte juridiske grunnlag for slike overføringer i dag.",
      },

      complaint: {
        title: "Retten til å klage",
        body:
          "Hvis du mener vi behandler personopplysningene dine på en måte " +
          "som bryter med personvernforordningen, kan du klage til " +
          "Datatilsynet. Du finner kontaktinfo og veiledning på " +
          "datatilsynet.no. Men ta gjerne kontakt med oss først — vi vil " +
          "heller fikse det enn at det blir en sak.",
      },

      whatWeSee: {
        title: "Hva vi faktisk kan se",
        body: "Når du laster opp en faktura, gjør vi to ting:",
        weSeeBullets: [
          "Vi leser PDF-en for å trekke ut transaksjonslinjene. Selve PDF-en " +
            "slettes umiddelbart etter at vi er ferdig — vi tar ikke vare på den.",
          "Vi lagrer linjene i databasen, men beskrivelsen av handelen " +
            '("Apotek 1", "Coop Mega", "Den norske kirke") blir kryptert ' +
            "før den havner der. Det betyr at den er låst med en nøkkel som " +
            "ikke ligger i databasen.",
        ],
        whatStaysVisible:
          "Beløp og dato er ikke kryptert. Vi trenger dem i klartekst for " +
          "å kunne regne ut hvem som skylder hvem. Men en utenforstående " +
          "som skulle få tak i databasen, vil bare se «350 kroner 12. mai» " +
          "— ikke hva eller hvor.",
      },

      whyItMatters: {
        title: "Hvorfor det er viktig",
        body:
          "Kortbeskrivelsene kan røpe ting som ikke angår noen andre enn deg: " +
          "et apotekbesøk avslører helse, et menighetstrekk røper religion, " +
          "en partistøtte sier noe om politisk overbevisning. Norsk personvern­lov " +
          "(personopplysningsloven, som følger GDPR) gir slik informasjon " +
          "ekstra beskyttelse. Vi har valgt å kryptere felter som kan inneholde " +
          "den typen detaljer, ikke fordi loven krever akkurat den løsningen, " +
          "men fordi det er den løsningen som faktisk gjør deg trygg.",
      },

      whoCantRead: {
        title: "Hvem kan ikke lese dataene dine",
        bullets: [
          "Vi som driver Fellesen kan ikke åpne databasen og kikke på " +
            "fakturalinjene dine. De er kryptert.",
          "Cloudflare, som leverer serverne våre, kan ikke lese innholdet.",
          "Neon, som hoster databasen, ser bare uleselige bytes der " +
            "handelsbeskrivelsene står.",
          "Andre brukere i andre husstander ser ingenting av det du har lastet opp.",
        ],
      },

      whoCanRead: {
        title: "Hvem kan lese dataene dine",
        bullets: [
          "Du selv, når du er logget inn.",
          "De andre medlemmene i din egen husstand — fordi det er hele " +
            "poenget med Fellesen. Alle medlemmer ser alle utgifter i " +
            "et oppgjør, også de du markerer som «ikke deles». " +
            "Markeringen betyr at utgiften ikke regnes inn i splitten — " +
            "ikke at den er skjult fra partneren. Det er sånn felles­ " +
            "økonomi fungerer: alle ser hva som ble brukt, men bare det " +
            "som er merket «deles» blir delt i regnestykket. Hvis du " +
            "ikke vil at husstanden skal se et kjøp, ikke legg det inn " +
            "i Fellesen.",
          "Ingen andre.",
        ],
      },

      helpers: {
        title: "Hvem vi samarbeider med",
        body:
          "Vi driver ikke alt selv. Noen få tjenester hjelper oss å levere " +
          "Fellesen. Alle har skrevet under på en databehandleravtale som " +
          "forplikter dem til samme nivå av personvern som vi gir deg.",
        bullets: [
          "Cloudflare driver serverne og lagrer fakturafilen i opptil et døgn " +
            "før den slettes. Cloudflares AI-tjeneste leser PDF-en for å " +
            "trekke ut transaksjonene, og trener ikke på dataene dine.",
          "Neon hoster databasen. De ser bare krypterte bytes der " + "handelsbeskrivelsene står.",
          "Google brukes hvis du velger «Logg inn med Google». De får vite " +
            "at du har en konto hos oss — ikke noe mer.",
          "Resend sender e-postene våre (invitasjoner og varsler). De ser " +
            "e-postadressen din og selve meldingen — ingen fakturadata.",
        ],
      },

      yourRights: {
        title: "Dine rettigheter",
        body:
          "Du har rett til å se alt vi har om deg, rette feil, og få slettet " +
          "kontoen din. Når du sletter kontoen din, sletter vi krypteringsnøkkelen " +
          "for husstanden din — etter det er fakturaene matematisk uleselige, " +
          "også for oss. Vi rydder også opp i de tomme radene som ligger igjen.",
        contactBefore: "Spørsmål, eller vil du bruke en av rettighetene dine? Skriv til ",
        contactEmail: "fellesen@klogt.no",
        contactAfter: ", så svarer vi innen 30 dager.",
        copyEmailHint: "Klikk for å kopiere",
        copyEmailDone: "Kopiert!",
      },

      honestLimits: {
        title: "Hva vi ikke kan love",
        body: "Vi vil heller være ærlige enn lure. Her er det vi ikke beskytter mot:",
        bullets: [
          "Hvis noen får tilgang til kontoen din (passord, telefon, e-post), " +
            "kan de se det du ser. Bruk en god innloggingsmetode og hold " +
            "enheten din trygg.",
          "Beløp og datoer er ikke kryptert. Noen som bryter seg inn i " +
            "databasen ser at noen brukte 350 kroner 12. mai, men ikke hva " +
            "eller hvor.",
          "Vi er en norsk tjeneste som lever under norsk lov. Hvis vi " +
            "skulle få en gyldig rettsavgjørelse, må vi følge den. Vi vil " +
            "alltid si fra om dette skjer, hvis loven tillater det.",
        ],
      },

      forTechies: {
        title: "For deg som vil ha de tekniske detaljene",
        body:
          "Vi krypterer med AES-256-GCM. Hver husstand har sin egen nøkkel, " +
          "som igjen er låst med en hovednøkkel som bare lever i " +
          "Cloudflares secrets-system. Vi har skrevet et detaljert dokument " +
          "med trusselmodell, GDPR-grunnlag og rutiner — det finnes på GitHub " +
          "i docs/security-posture.md.",
      },
    },
  },

  cards: {
    profileTitle: "Mine kort",
    profileDescription:
      "Koble til kortene dine via BankID. Du velger selv hvilke husstander " +
      "som får se transaksjonene fra hvert kort.",
    emptyTitle: "Ingen kort tilkoblet enda",
    emptyDescription:
      "Når du kobler til et kort autentiserer du med BankID. Etterpå kan du " +
      "velge hvilke husstander som skal få inn transaksjoner fra det kortet.",
    connectButton: "Koble til kort",
    connectDialog: {
      title: "Koble til nytt kort",
      description:
        "Du blir omdirigert til kortleverandøren for å autentisere med BankID. " +
        "Vi får tilgang til transaksjoner, ikke kortnummer eller passord.",
      countryLabel: "Land",
      issuerLabel: "Kortleverandører",
      issuerPlaceholder: "Søk etter SAS Mastercard, SEB Kort, …",
      issuerEmpty: "Ingen utstedere matchet søket.",
      loadIssuersFailed: (msg: string) => `Klarte ikke å hente utstedere: ${msg}`,
      alreadyConnectedHint: "Allerede tilkoblet",
      cancel: "Avbryt",
      connect: "Fortsett til BankID",
      connectFailed: (msg: string) => `Klarte ikke å starte tilkoblingen: ${msg}`,
      redirecting: "Sender deg til BankID — du kommer tilbake hit når koblingen er godkjent…",
    },
    status: {
      // Tre-trinns tilstand utledet i frontend fra `status` + `expiresAt`:
      //   - active  : BankID-samtykke gyldig og >14 dager til utløp
      //   - renewal : gyldig, men ≤14 dager til utløp (`needsRenewal`)
      //   - expired : `expires_at` passert eller `status` ikke "active"
      // "inactive" beholdes for bakoverkompatibilitet (eldre kall).
      active: "Aktiv",
      renewal: "Trenger fornyelse",
      expired: "Utløpt",
      inactive: "Inaktiv",
    },
    lastSyncedNever: "Aldri synkronisert",
    lastSyncedReady: "Klar til bruk",
    lastSyncedAt: (when: string) => `Synkronisert ${when}`,
    expiresIn: (when: string) => `Utløper ${when}`,
    expiresExpired: "Utløpt — krever ny BankID-autentisering",
    expiresExpiredCta: "Trenger ny BankID-autentisering",
    accountsTitle: "Kontoer",
    accountsEmpty: "Ingen kontoer registrert enda.",
    // Ekstrakort: et kort tilhører samme bankkonto som hovedkortet, men
    // brukes av en annen person (typisk samboer / familie). Vi viser hvert
    // observerte kort separat under Mine kort, med tilordning til riktig
    // husstandsmedlem så transaksjonene blir attribuert korrekt.
    cardLabels: {
      mainCard: "Hovedkort",
      mainCardYou: "Hovedkort · Meg",
      extraCard: "Ekstrakort",
      extraCardAssignPrompt: "Hvem bruker dette kortet?",
      belongsTo: (name: string) => `Tilhører ${name}`,
      change: "Endre",
      remove: "Fjern tilordning",
      pickMember: "Velg medlem",
      assignSuccess: (name: string, lastFour: string | null) =>
        lastFour ? `Tilordnet ··${lastFour} til ${name}` : `Tilordnet kortet til ${name}`,
      assignFailed: (msg: string) => `Klarte ikke å tilordne kortet: ${msg}`,
      unassignSuccess: "Tilordning fjernet",
      unassignFailed: (msg: string) => `Klarte ikke å fjerne tilordning: ${msg}`,
      loadFailed: (msg: string) => `Klarte ikke å laste kortene på denne tilkoblingen: ${msg}`,
    },
    actions: {
      reconnect: "Autentiser på nytt",
      reconnectDescription:
        "BankID-autentiseringen utløper etter 90 dager. Du kan fornye når som helst.",
      disconnect: "Koble fra",
      disconnectConfirm: (name: string) =>
        `Vil du koble fra ${name}? Aktive oppgjør beholder transaksjonene sine, men ingen nye hentes inn.`,
      disconnectFailed: (msg: string) => `Klarte ikke å koble fra: ${msg}`,
    },
    sharing: {
      title: "Delt med husstander",
      empty: "Ikke delt med noen husstander.",
      description:
        "Når du deler et kort med en husstand kan medlemmene se kortet — " +
        "men ingen transaksjoner trekkes inn før du eksplisitt kobler kortet til et oppgjør.",
      toggleLabel: (name: string) => `Del med ${name}`,
      shareFailed: (msg: string) => `Klarte ikke å dele: ${msg}`,
      unshareFailed: (msg: string) => `Klarte ikke å trekke tilbake delingen: ${msg}`,
    },
    postShare: {
      title: (aspspName: string) => `Hent transaksjoner fra ${aspspName}?`,
      description: (groupName: string, monthLabel: string) =>
        `Vi kan opprette et oppgjør for ${monthLabel} i ${groupName} og fylle det med ` +
        `dagens transaksjoner. Du kan også koble til senere fra oppgjørs-visningen.`,
      confirm: "Ja, opprett og hent",
      dismiss: "Bare del, ikke nå",
      fetching: "Henter transaksjoner …",
      successTitle: (fetched: number) =>
        fetched === 1 ? "1 transaksjon hentet" : `${fetched} transaksjoner hentet`,
      successDescription: (groupName: string) =>
        `Se oppgjøret under ${groupName} for å fordele utgifter.`,
      failed: (msg: string) => `Klarte ikke å hente transaksjoner: ${msg}`,
    },
    cardSources: {
      title: "Kortkilder",
      empty: "Ingen kort er koblet til dette oppgjøret enda.",
      addCard: "Legg til kort",
      refresh: "Oppdater",
      refreshSuccess: (fetched: number) =>
        fetched === 0
          ? "Ingen nye transaksjoner."
          : fetched === 1
            ? "1 transaksjon oppdatert."
            : `${fetched} transaksjoner oppdatert.`,
      refreshFailed: (msg: string) => `Klarte ikke å oppdatere: ${msg}`,
      ownerLabel: (name: string) => (name ? `Koblet til av ${name}` : "Eier ukjent"),
      lastSync: (relative: string) => `sist oppdatert ${relative}`,
      neverSynced: "ikke synkronisert ennå",
      readyToUse: "klar til bruk",
      needsRenewalLabel: "Trenger fornyelse",
      expiredLabel: "Utløpt",
      manageInMyCards: "Forny i Mine kort",
      unlinkLabel: (aspspName: string) => `Fjern ${aspspName} fra dette oppgjøret`,
      unlinkFailed: (msg: string) => `Klarte ikke å fjerne kortet: ${msg}`,
      liveTxSummary: (count: number) =>
        count === 1
          ? "1 live-transaksjon i denne perioden."
          : `${count} live-transaksjoner i denne perioden.`,
      pickerTitle: "Velg kort å koble til",
      pickerDescription:
        "Velg blant kortene dine eller kort andre i husstanden har delt. Udelte kort deles automatisk når du kobler dem til.",
      pickerClose: "Lukk",
      pickerEmpty:
        "Ingen kort er tilgjengelig. Koble til et kort under Mine kort først, eller be et annet medlem av husstanden om å dele sitt.",
      pickerNotSharedHint: "Ikke delt med husstanden enda",
      linkAction: "Koble til",
      linkActionShareAndAdd: "Del og koble til",
      linkSuccess: (fetched: number) =>
        fetched === 0
          ? "Kort koblet til. Ingen transaksjoner i perioden."
          : fetched === 1
            ? "Kort koblet til. 1 transaksjon hentet."
            : `Kort koblet til. ${fetched} transaksjoner hentet.`,
      linkFailed: (msg: string) => `Klarte ikke å koble til kortet: ${msg}`,
      shareFailed: (msg: string) => `Klarte ikke å dele kortet: ${msg}`,
    },
    callback: {
      processingTitle: "Fullfører tilkobling",
      processingDescription:
        "Du er signert inn med BankID. Vi henter nå kontoinformasjon fra banken — " +
        "siden oppdaterer seg automatisk når det er ferdig.",
      successTitle: "Kortet er tilkoblet",
      successDescription: (accountCount: number) =>
        accountCount === 1
          ? "1 konto ble registrert. Du kan nå velge hvilke husstander som skal se transaksjonene."
          : `${accountCount} kontoer ble registrert. Du kan nå velge hvilke husstander som skal se transaksjonene.`,
      successAlreadyActive:
        "Kortet er allerede tilkoblet. Du kan se det og velge hvilke husstander som skal se transaksjonene under Mine kort.",
      successAction: "Gå til Mine kort",
      errorTitle: "Tilkobling feilet",
      errorMissingParams:
        "Vi mangler informasjon fra kortleverandøren. Start tilkoblingen på nytt.",
      errorGeneric: (msg: string) => `Noe gikk galt: ${msg}`,
      retryAction: "Tilbake til Mine kort",
    },
  },
} as const;

export type Strings = typeof nb;
